import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * Shopify caps `{% paginate %}` at 50 items per page, so the grid is rendered 40 products per server page and
 * the browser loads PAGES_PER_BATCH server pages at once (3 x 40 = 120 products per "page" for the shopper).
 */
const PAGES_PER_BATCH = 3;

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** Guards against two batches (idle fill + scroll) appending the same pages twice. */
  #batchInFlight = false;

  /** How many times the missing pages of the first numbered page were retried. */
  #retries = 0;

  /** Identifies the newest numbered-page navigation so a slow, older one never overwrites it. */
  #navToken = 0;

  /** The URL search (without `page`) the grid was rendered for; tells a page change from a filter change. */
  #baseKey = '';

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#observeViewMore();
    this.#fillFirstBatch();
    this.#baseKey = this.#searchWithoutPage();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.addEventListener('facets:rendered', this.#handleFacetsRendered);

    // Numbered pagination without a page reload (the server-rendered links still work without JavaScript).
    this.addEventListener('click', this.#handleNavClick);
    this.addEventListener('pointerover', this.#handleNavHover, { passive: true });
    this.addEventListener('touchstart', this.#handleNavHover, { passive: true });
    window.addEventListener('popstate', this.#handlePopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.removeEventListener('facets:rendered', this.#handleFacetsRendered);
    this.removeEventListener('click', this.#handleNavClick);
    this.removeEventListener('pointerover', this.#handleNavHover);
    this.removeEventListener('touchstart', this.#handleNavHover);
    window.removeEventListener('popstate', this.#handlePopState);
  }

  /**
   * Tops the grid up to a full batch (120 products) right after the first paint, without touching the history.
   * Only one server page is server-rendered so the initial HTML (and LCP) stay small.
   *
   * With numbered pagination (no infinite scroll sentinel) the batch is the group of PAGES_PER_BATCH server pages the
   * current page belongs to (1-3, 4-6, ...); missing pages are added before / after the rendered one, in order.
   */
  #fillFirstBatch() {
    if (this.#customViewActive()) return;
    if (!this.refs.viewMoreNext) {
      this.#completeNumberedBatch();
      return;
    }
    requestIdleCallback(() => {
      this.#renderNextBatch(PAGES_PER_BATCH - 1, { pushHistory: false });
    });
    this.#fetchPage('previous');
  }

  async #completeNumberedBatch() {
    const { grid, cards } = this.refs;
    if (!grid || !Array.isArray(cards) || !cards.length || this.#batchInFlight) return;

    const lastPage = Number(grid.dataset.lastPage) || 1;
    const present = new Set(cards.map((card) => Number(card.dataset.page)));
    const current = Number(cards[0].dataset.page) || 1;
    const first = Math.floor((current - 1) / PAGES_PER_BATCH) * PAGES_PER_BATCH + 1;
    const last = Math.min(first + PAGES_PER_BATCH - 1, lastPage);

    /** @type {number[]} */
    const missing = [];
    for (let page = first; page <= last; page++) if (!present.has(page)) missing.push(page);
    if (!missing.length) {
      this.#prefetchAround(first);
      return;
    }

    this.#batchInFlight = true;
    try {
      await Promise.all(
        missing.map((page) => this.pages.has(page) || this.#fetchSpecificPage(page).catch(() => {}))
      );

      // Filters changed, or the rating sort took over the grid, while the pages were loading.
      if (this.refs.grid !== grid || this.#customViewActive()) return;

      /** @type {Element[]} */
      const before = [];
      /** @type {Element[]} */
      const after = [];
      for (const page of missing) {
        const elements = this.#getGridForPage(page);
        if (elements) (page < current ? before : after).push(...elements);
      }

      grid.prepend(...before);
      grid.append(...after);
      this.#aspectRatioHelper?.processNewElements();
    } finally {
      this.#batchInFlight = false;
    }

    // A page that could not be loaded (store busy): try again a little later, a few times.
    if (missing.some((page) => !this.pages.has(page)) && this.#retries++ < 3) {
      setTimeout(() => this.#completeNumberedBatch(), 2500 * this.#retries);
      return;
    }

    // Warm the neighbouring numbered pages so that clicking one is instant.
    this.#prefetchAround(this.#currentBatchFirstPage());
  }

  /* ------------------------------------------------------------------ numbered pagination (no reload) */

  /** @returns {string} the URL search string without the `page` parameter */
  #searchWithoutPage() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    return url.searchParams.toString();
  }

  /** @returns {number} the first server page of the numbered page that is on screen (1, 4, 7, ...) */
  #currentBatchFirstPage() {
    const { cards } = this.refs;
    const first = Array.isArray(cards) && cards[0] ? Number(cards[0].dataset.page) || 1 : 1;
    return Math.floor((first - 1) / PAGES_PER_BATCH) * PAGES_PER_BATCH + 1;
  }

  /** Cancels an older #prefetchAround run when filters change or a newer run starts. */
  #prefetchRun = 0;

  /** Keeps warmed images referenced so the browser does not drop them. */
  #warmed = [];

  /**
   * Fetches (in parallel, into the cache) the server pages of the numbered page that starts at `firstPage`.
   * @param {number} firstPage
   * @param {{ warm?: boolean }} [options] also download the first image of every card
   * @returns {Promise<void>}
   */
  async #prefetchBatch(firstPage, { warm = false } = {}) {
    if (this.#customViewActive()) return;
    const jobs = [];
    for (let i = 0; i < PAGES_PER_BATCH; i++) {
      const page = firstPage + i;
      if (!this.#shouldUsePage({ page })) continue;
      jobs.push(this.pages.has(page) ? Promise.resolve() : this.#fetchSpecificPage(page).catch(() => {}));
    }
    await Promise.all(jobs);
    if (!warm) return;
    for (let i = 0; i < PAGES_PER_BATCH; i++) this.#warmImages(firstPage + i);
  }

  /** Starts downloading the main card image of a cached server page (detached lazy images never load by themselves). */
  #warmImages(page) {
    const elements = this.#getGridForPage(page);
    if (!elements) return;
    for (const card of elements) {
      const img = card.querySelector('img');
      if (!img) continue;
      const warm = new Image();
      warm.decoding = 'async';
      warm.fetchPriority = 'low';
      const sizes = (img.getAttribute('sizes') || '').replace(/^\s*auto\s*,?\s*/, '');
      if (sizes) warm.sizes = sizes;
      const srcset = img.getAttribute('srcset');
      if (srcset) warm.srcset = srcset;
      warm.src = img.getAttribute('src') || '';
      this.#warmed.push(warm);
    }
    if (this.#warmed.length > 600) this.#warmed.splice(0, this.#warmed.length - 600);
  }

  /**
   * Warms the numbered pages around the one on screen: next, previous, next+1, previous-1 (one after another so
   * they never compete with what the shopper is looking at). Images are warmed for the nearest two only.
   * @param {number} firstPage first server page of the numbered page on screen
   */
  async #prefetchAround(firstPage) {
    if (this.#customViewActive()) return;
    const run = ++this.#prefetchRun;
    const saveData = Boolean(/** @type {any} */ (navigator).connection?.saveData);
    if (saveData) return;

    const step = PAGES_PER_BATCH;
    // Idle callbacks can starve while the page is busy (scrolling, images): never wait longer than 300 ms.
    const idle = () => new Promise((resolve) => requestIdleCallback(resolve, { timeout: 300 }));
    const usable = (page) => page >= 1 && this.#shouldUsePage({ page });

    // The two neighbouring numbered pages first, together (with their images), then the ones after them.
    await idle();
    if (run !== this.#prefetchRun || this.#customViewActive()) return;
    await Promise.all(
      [firstPage + step, firstPage - step].filter(usable).map((page) => this.#prefetchBatch(page, { warm: true }))
    );
    for (const page of [firstPage + 2 * step, firstPage - 2 * step]) {
      if (run !== this.#prefetchRun || this.#customViewActive()) return;
      if (!usable(page)) continue;
      await idle();
      if (run !== this.#prefetchRun) return;
      await this.#prefetchBatch(page, { warm: false });
    }
  }

  /** @param {Event} event */
  #handleNavHover = (event) => {
    if (this.#customViewActive() || !(event.target instanceof Element)) return;
    const link = event.target.closest('nav.pagination--batch:not([data-cv-nav]) a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;
    const page = Number(new URL(link.href, window.location.href).searchParams.get('page')) || 1;
    this.#prefetchBatch(page, { warm: true });
  };

  /** @param {MouseEvent} event */
  #handleNavClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    if (this.#customViewActive() || !(event.target instanceof Element)) return;

    const link = event.target.closest('nav.pagination--batch:not([data-cv-nav]) a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    this.#goToBatch(new URL(link.href, window.location.href), { push: true });
  };

  #handlePopState = () => {
    if (this.#customViewActive() || this.#searchWithoutPage() !== this.#baseKey) return;
    this.#goToBatch(new URL(window.location.href), { push: false });
  };

  /**
   * Shows the numbered page whose first server page is the `page` parameter of `url`: its 3 server pages are
   * swapped into the grid straight from the cache when they were prefetched, otherwise fetched in parallel.
   *
   * @param {URL} url
   * @param {{ push: boolean }} options
   */
  async #goToBatch(url, { push }) {
    const { grid } = this.refs;
    if (!grid) return;

    const lastPage = Number(grid.dataset.lastPage) || 1;
    const requested = Math.min(Math.max(Number(url.searchParams.get('page')) || 1, 1), lastPage);
    const firstPage = Math.floor((requested - 1) / PAGES_PER_BATCH) * PAGES_PER_BATCH + 1;
    if (firstPage === this.#currentBatchFirstPage() && grid.children.length >= 1 && push) return;

    // Remember the cards that are on screen (the first page was rendered by the server, not fetched).
    const shown = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
    for (const page of new Set(shown.map((card) => Number(card.dataset.page)))) {
      if (this.pages.has(page)) continue;
      const html = shown.filter((card) => Number(card.dataset.page) === page).map((card) => card.outerHTML).join('');
      this.pages.set(page, `<ul ref="grid">${html}</ul>`);
    }

    const token = ++this.#navToken;
    /** @type {number[]} */
    const pageNumbers = [];
    for (let i = 0; i < PAGES_PER_BATCH; i++) if (firstPage + i <= lastPage) pageNumbers.push(firstPage + i);

    const needsFetch = pageNumbers.some((page) => !this.pages.has(page));
    if (needsFetch) this.setAttribute('data-facets-loading', '');

    try {
      await Promise.all(
        pageNumbers.map((page) => this.pages.has(page) || this.#fetchSpecificPage(page).catch(() => {}))
      );
      if (token !== this.#navToken || this.refs.grid !== grid || this.#customViewActive()) return;

      /** @type {Element[]} */
      const elements = [];
      for (const page of pageNumbers) {
        const pageElements = this.#getGridForPage(page);
        if (pageElements) elements.push(...pageElements);
      }
      if (!elements.length) {
        if (push) window.location.assign(url.toString());
        return;
      }

      // First cards at once, the rest in small animation-frame chunks so the click paints immediately.
      const FIRST = 24;
      const CHUNK = 24;
      grid.replaceChildren(...elements.slice(0, FIRST));
      this.#aspectRatioHelper?.processNewElements();
      const rest = elements.slice(FIRST);
      if (rest.length) {
        const appendChunk = () => {
          if (token !== this.#navToken || this.refs.grid !== grid) return;
          grid.append(...rest.splice(0, CHUNK));
          if (rest.length) requestAnimationFrame(appendChunk);
          else this.#aspectRatioHelper?.processNewElements();
        };
        requestAnimationFrame(appendChunk);
      }

      if (push) {
        url.hash = '';
        history.pushState('', '', url.toString());
        this.style.scrollMarginTop = 'calc(var(--header-height, 0px) + 8px)';
        this.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
      this.#baseKey = this.#searchWithoutPage();
      this.#updateNav(firstPage);

      this.#prefetchAround(firstPage);
    } finally {
      if (token === this.#navToken) this.removeAttribute('data-facets-loading');
    }
  }

  /**
   * Re-draws the server-rendered numbered navigation for the numbered page that starts at `firstPage`
   * (same markup as snippets/batch-pagination.liquid, built from the data attributes of that nav).
   * @param {number} firstPage
   */
  #updateNav(firstPage) {
    for (const nav of this.querySelectorAll('nav.pagination--batch:not([data-cv-nav])')) {
      if (!(nav instanceof HTMLElement)) continue;
      const per = Number(nav.dataset.pagesPerBatch) || PAGES_PER_BATCH;
      const batches = Number(nav.dataset.batches) || 1;
      const head = nav.dataset.urlHead ?? '';
      const tail = nav.dataset.urlTail ?? '';
      const current = Math.floor((firstPage - 1) / per) + 1;

      const leftIcon = nav.querySelector('li:first-child svg')?.outerHTML ?? '';
      const rightIcon = nav.querySelector('li:last-child svg')?.outerHTML ?? '';
      const prevLabel = nav.querySelector('li:first-child [aria-label]')?.getAttribute('aria-label') ?? 'Previous';
      const nextLabel = nav.querySelector('li:last-child [aria-label]')?.getAttribute('aria-label') ?? 'Next';
      const pageLabel = nav.querySelector('a.pagination__link--page')?.getAttribute('aria-label') ?? 'Page 1';

      const href = (number) => `${head}page=${(number - 1) * per + 1}${tail}`;
      const arrow = (number, label, icon, enabled) =>
        enabled
          ? `<li class="pagination__item"><a href="${href(number)}" aria-label="${label}" class="pagination__link pagination__link--arrow">${icon}</a></li>`
          : `<li class="pagination__item"><span aria-label="${label}" class="pagination__link pagination__link--arrow pagination__link--disabled" aria-disabled="true">${icon}</span></li>`;

      let html = arrow(current - 1, prevLabel, leftIcon, current > 1);
      let gapShown = false;
      for (let number = 1; number <= batches; number++) {
        if (number === 1 || number === batches || Math.abs(number - current) <= 1) {
          gapShown = false;
          html +=
            number === current
              ? `<li class="pagination__item"><span class="pagination__link pagination__link--page pagination__link--current" aria-current="page">${number}</span></li>`
              : `<li class="pagination__item"><a href="${href(number)}" aria-label="${pageLabel.replace(/\d+/, String(number))}" class="pagination__link pagination__link--page">${number}</a></li>`;
        } else if (!gapShown) {
          gapShown = true;
          html +=
            '<li class="pagination__item pagination__item--gap"><span class="pagination__link pagination__link--gap" aria-hidden="true">&hellip;</span></li>';
        }
      }
      html += arrow(current + 1, nextLabel, rightIcon, current < batches);

      const list = nav.querySelector('.pagination__list');
      if (list) list.innerHTML = html;
      nav.dataset.current_page = String(current);
    }
  }

  #handleFacetsRendered = () => {
    if (!this.isConnected) return;
    this.#baseKey = this.#searchWithoutPage();
    this.#observeViewMore();
    this.#fillFirstBatch();
  };

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    // `view=cards` renders the section without the (slow) filter block: only the product cards are needed here.
    const fetchUrl = new URL(pageInfo.url.toString());
    fetchUrl.searchParams.set('view', 'cards');
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, fetchUrl);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * Called by the infinite scroll observer: appends the next full batch (3 server pages = 120 products).
   */
  #renderNextPage() {
    return this.#renderNextBatch(PAGES_PER_BATCH);
  }

  /**
   * Fetches up to `count` following server pages in parallel and appends them in order.
   *
   * @param {number} count - How many server pages to append
   * @param {{ pushHistory?: boolean }} [options]
   */
  async #renderNextBatch(count, { pushHistory = true } = {}) {
    const { grid } = this.refs;
    if (!grid || this.#batchInFlight || this.#customViewActive()) return;

    const first = this.#getPage('next');
    if (!first || !this.#shouldUsePage(first)) return;

    this.#batchInFlight = true;

    try {
      /** @type {number[]} */
      const pageNumbers = [];
      for (let i = 0; i < count; i++) {
        const pageNumber = first.page + i;
        if (!this.#shouldUsePage({ page: pageNumber })) break;
        pageNumbers.push(pageNumber);
      }

      // All pages of the batch are requested at the same time instead of one after another.
      await Promise.all(pageNumbers.map((pageNumber) => this.pages.has(pageNumber) || this.#fetchSpecificPage(pageNumber)));

      // Filters changed, or collection-metafield-sort.js took over the grid, while the pages were loading.
      if (this.refs.grid !== grid || this.#customViewActive()) return;

      /** @type {Element[]} */
      const elements = [];
      let lastRenderedPage = first.page;

      for (const pageNumber of pageNumbers) {
        const pageElements = this.#getGridForPage(pageNumber);
        if (!pageElements) break;
        elements.push(...pageElements);
        lastRenderedPage = pageNumber;
      }

      if (!elements.length) return;

      grid.append(...elements);
      this.#aspectRatioHelper?.processNewElements();

      if (pushHistory) {
        const url = new URL(window.location.href);
        url.searchParams.set('page', lastRenderedPage.toString());
        url.hash = '';
        history.pushState('', '', url.toString());
      }

      requestIdleCallback(() => this.#prefetchNextBatch());
    } finally {
      this.#batchInFlight = false;
      // The sentinel may still be in view: re-observe so the observer reports it again.
      this.#rearmViewMoreNext();
    }
  }

  /**
   * The rating sorts and the Collection / Sub-collection filters (collection-metafield-sort.js) render and page the grid themselves.
   * @returns {boolean}
   */
  #customViewActive() {
    const params = new URL(window.location.href).searchParams;
    const sort = params.get('sort_by');
    return sort === 'rating-desc' || sort === 'rating-count-desc' || params.has('cv_col') || params.has('cv_sub');
  }

  #rearmViewMoreNext() {
    const { viewMoreNext } = this.refs;
    if (!viewMoreNext || !this.infinityScrollObserver) return;
    this.infinityScrollObserver.unobserve(viewMoreNext);
    this.infinityScrollObserver.observe(viewMoreNext);
  }

  /**
   * Warms the cache with the next batch of server pages so the following scroll step is instant.
   */
  #prefetchNextBatch() {
    const next = this.#getPage('next');
    if (!next) return;
    for (let i = 0; i < PAGES_PER_BATCH; i++) {
      const pageNumber = next.page + i;
      if (!this.#shouldUsePage({ page: pageNumber }) || this.pages.has(pageNumber)) continue;
      this.#fetchSpecificPage(pageNumber);
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.#retries = 0;
    this.#prefetchRun++;
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // The grid is refilled when facets.js dispatches `facets:rendered` (after the new HTML has been morphed in).
  };
}
